/* eslint react/prop-types: 0 */
import React from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  Box,
  Typography,
} from '@mui/material';

// Resolve a value for a given field, preferring the column's valueGetter
// when defined so we stay aligned with desktop rendering.
const resolveValue = (row, field, column) => {
  if (!row || !field) return null;
  if (column && typeof column.valueGetter === 'function') {
    try {
      return column.valueGetter({ row, value: row[field], field });
    } catch {
      return row[field];
    }
  }
  return row[field];
};

const renderFieldValue = (row, field, columnsByField) => {
  const column = columnsByField?.[field];
  const value = resolveValue(row, field, column);
  if (value === null || value === undefined || value === '') return null;
  if (React.isValidElement(value)) return value;
  if (value instanceof Date) return value.toLocaleString();
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

/**
 * ResponsiveListCard
 *
 * Default card layout used by ResponsiveList when the consumer doesn't
 * provide a `renderMobileCard` render prop. Surfaces a primary field plus
 * a few secondary fields. If neither `primaryField` nor `secondaryFields`
 * is given, falls back to column definitions flagged `mobilePrimary: true`.
 */
function ResponsiveListCard({
  row,
  columns = [],
  primaryField,
  secondaryFields,
  onClick,
  sx,
}) {
  const columnsByField = React.useMemo(() => {
    const map = {};
    columns.forEach((c) => {
      if (c?.field) map[c.field] = c;
    });
    return map;
  }, [columns]);

  let resolvedPrimary = primaryField;
  let resolvedSecondary = secondaryFields;

  if (!resolvedPrimary && !resolvedSecondary) {
    const flagged = columns.filter((c) => c?.mobilePrimary);
    if (flagged.length > 0) {
      resolvedPrimary = flagged[0].field;
      resolvedSecondary = flagged.slice(1).map((c) => c.field);
    }
  }

  const primaryValue = resolvedPrimary
    ? renderFieldValue(row, resolvedPrimary, columnsByField)
    : null;

  const secondaryValues = (resolvedSecondary || [])
    .map((field) => ({
      field,
      label: columnsByField[field]?.headerName,
      value: renderFieldValue(row, field, columnsByField),
    }))
    .filter((entry) => entry.value !== null && entry.value !== undefined);

  const body = (
    <CardContent>
      {primaryValue !== null && (
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 'medium', mb: secondaryValues.length ? 0.5 : 0 }}
        >
          {primaryValue}
        </Typography>
      )}
      {secondaryValues.map((entry) => (
        <Box
          key={entry.field}
          sx={{ mt: 0.25 }}
        >
          {entry.label && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mr: 0.5 }}
            >
              {`${entry.label}:`}
            </Typography>
          )}
          <Typography
            variant="body2"
            component="span"
          >
            {entry.value}
          </Typography>
        </Box>
      ))}
    </CardContent>
  );

  return (
    <Card sx={sx}>
      {onClick ? <CardActionArea onClick={onClick}>{body}</CardActionArea> : body}
    </Card>
  );
}

export default ResponsiveListCard;
